import './PinPairing.scss';
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router';
import cx from 'classnames';
import { userStore } from '../../store';
import { useTranslation } from 'react-i18next';
import Modal from '../modal/Modal';
import { ROUTES } from '../../js/_constants';
import api from '../../api';
import {
    cond,
    curry,
    equals,
    ifElse,
    isEmpty,
    pipe,
    test,
    update
} from 'ramda';
import { isNotEmpty } from '../../js/fpUtils';
import ErrorMessage from '../errorMessage/ErrorMessage';
import { type DropdownOptionType } from '../multiSelectDropdown/MultiSelectDropdown';
import DealershipDropdown from '../dealershipDropdown/DealershipDropdown';
import helpers from '../../js/helpers';
import { type Device } from '../../api/types/index';

enum PinPairingErrors {
    Expired = 'This pin is expired.',
    NotFound = 'Pin does not exist.',
    Default = 'Pin pairing failed.'
}

type PinPairingProps = {
    onClose: (param: { message: string }) => void;
    isOpen: boolean;
};

const PinPairing = ({ onClose, isOpen }: PinPairingProps) => {
    const { t } = useTranslation();
    const{ dealerships } = userStore();
    const [dealerId, setDealerId] = useState('');
    const [pin, setPin] = useState<(string | number)[]>([
        '',
        '',
        '',
        '',
        '',
        ''
    ]);
    const [newDevice, setNewDevice] = useState<Device | undefined>();
    const [errorText, setErrorText] = useState('');
    const [isPairing, setIsPairing] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const pin0Ref = useRef<HTMLInputElement>(null);
    const pin1Ref = useRef<HTMLInputElement>(null);
    const pin2Ref = useRef<HTMLInputElement>(null);
    const pin3Ref = useRef<HTMLInputElement>(null);
    const pin4Ref = useRef<HTMLInputElement>(null);
    const pin5Ref = useRef<HTMLInputElement>(null);
    const inputRefs = [pin0Ref, pin1Ref, pin2Ref, pin3Ref, pin4Ref, pin5Ref];

    const handleDealerSelection = (selectedOptions: DropdownOptionType[]) => {
        if (isNotEmpty(selectedOptions)) {
            const dealerIri = selectedOptions[0].value;
            setDealerId(dealerIri);
        } else {
            setDealerId('');
        }
    };

    const isNotCompletePin = () => pin.filter(Boolean).length < 6;
    const handlePinPairing = async () => {
        if (isNotCompletePin()) return setErrorText('PIN is not complete.');

        try {
            setIsPairing(true);

            const response = await api.pairDevice({
                dealerIri: dealerId,
                pin: pin.join('')
            });

            if (response.ok) {
                const newDevice = (await response.json()) as Device;

                setNewDevice(newDevice);
                setIsSuccess(true);
            } else {
                // Throw new error which will be caught below
                // In case pairing API call fails
                const errors = await response.json();

                if (errors.violations && errors.violations.length > 0) {
                    throw new Error(errors.violations[0].message);
                } else {
                    throw new Error(PinPairingErrors.Default);
                }
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                switch (error.message) {
                    case PinPairingErrors.Expired:
                        setErrorText(t('pin_pairing_pin_expired'));
                        break;
                    case PinPairingErrors.NotFound:
                        setErrorText(t('pin_pairing_pin_not_found'));
                        break;
                    /**
                     * Once new pin pairing error handling has been fixed,
                     * we shall revisit this logic.
                     */
                    // case 0:
                    //     setErrorText(t('pin_pairing_device_already_paired'));
                    //     break;
                    default:
                        setErrorText(t('pin_pairing_failed'));
                        break;
                }
            }
        } finally {
            setIsPairing(false);
        }
    };

    enum KeyboardCodes {
        Backspace = 'Backspace',
        ArrowLeft = 'ArrowLeft',
        ArrowRight = 'ArrowRight',
        Enter = 'Enter',
        AnyDigit = 'Digit'
    }

    const moveFocusLeft = (i: number) => inputRefs[i - 1]?.current?.focus();
    const moveFocusRight = (i: number) => inputRefs[i + 1]?.current?.focus();
    const updateNumber = (
        i: number,
        event: React.KeyboardEvent<HTMLInputElement>
    ) => update(i, event.key, pin);
    const currentNumberIsEmpty = (i: number) => isEmpty(pin[i]);
    const deletePreviousNumber = (i: number) => update(i - 1, '', pin);
    const deleteCurrentNumber = (i: number) => update(i, '', pin);
    const updatePinState = (nextPin: (string | number)[]) => {
        if (errorText) setErrorText('');
        setPin(nextPin);
    };

    const handlePinChange = (
        i: number,
        event: React.KeyboardEvent<HTMLInputElement>
    ) =>
        pipe(
            () => updateNumber(i, event),
            updatePinState,
            () => moveFocusRight(i)
        )();

    const handlePinDelete = (i: number) =>
        pipe(
            ifElse(
                currentNumberIsEmpty,
                deletePreviousNumber,
                deleteCurrentNumber
            ),
            updatePinState,
            () => moveFocusLeft(i)
        )(i);

    const handleKeyDown = curry(
        (index: number, event: React.KeyboardEvent<HTMLInputElement>) =>
            cond([
                [equals(KeyboardCodes.ArrowRight), () => moveFocusRight(index)],
                [equals(KeyboardCodes.ArrowLeft), () => moveFocusLeft(index)],
                [equals(KeyboardCodes.Backspace), () => handlePinDelete(index)],
                [
                    test(new RegExp(KeyboardCodes.AnyDigit)),
                    () => handlePinChange(index, event)
                ]
            ])(event.code as never)
    );

    useEffect(() => {
        if (dealerships.list.length === 1) {
            setDealerId(dealerships.list[0]['@id']);
        }
    }, []);

    return (
        <Modal
            className="b-pin-pairing"
            disabled={
                isEmpty(dealerId) || isNotCompletePin() || Boolean(newDevice)
            }
            localeAction={t('device_pair_action')}
            isLoading={isPairing}
            onAction={handlePinPairing}
            onCancel={
                isSuccess ? () => onClose({ message: 'success' }) : onClose
            }
            onClose={
                isSuccess ? () => onClose({ message: 'success' }) : onClose
            }
            show={isOpen}
        >
            <h4 className="pin-title">
                Chromebox / Retail Player PIN Pairing:
            </h4>
            <form className="pin-list" name="pinList">
                {[0, 1, 2, 3, 4, 5].map((number: number, index: number) => (
                    <input
                        key={`pin-value${number}`}
                        ref={inputRefs[index]}
                        className={cx('pin-value', index)}
                        type="text"
                        maxLength={1}
                        name={`pin${index}`}
                        onKeyDown={handleKeyDown(index)}
                        onChange={handleKeyDown(index)}
                        value={pin[index]}
                        disabled={isSuccess || isPairing}
                    />
                ))}
            </form>
            <div>
                <DealershipDropdown
                    onChange={handleDealerSelection}
                    defaultOptions={
                        dealerships ? helpers.mapDealershipOptions(dealerships.list) : []
                    }
                    defaultValue={
                        dealerships && dealerships.list.length === 1
                            ? [
                                  {
                                      label: dealerships.list[0].title,
                                      value: dealerships.list[0]['@id']
                                  }
                              ]
                            : []
                    }
                    disabled={isSuccess || isPairing}
                />
            </div>
            {newDevice && (
                <>
                    <span className="b-pin-pairing__success-message">
                        {t('device_registered_successfully')}
                    </span>
                    <span className="b-pin-pairing__go-to-device">
                        Go to device{' '}
                        {
                            <Link
                                to={`${ROUTES.DEALERSHIPS}${ROUTES.DEVICES}/${newDevice.deviceNumber}?brand=${newDevice.brand.split('/').pop()}`}
                                className="b-pin-pairing__link-to-device"
                            >
                                {newDevice.deviceNumber}
                            </Link>
                        }
                    </span>
                </>
            )}
            {errorText && <ErrorMessage message={errorText} />}
        </Modal>
    );
};

export default PinPairing;
